import React from 'react'
import { createPortal } from 'react-dom'
import { createRoot } from 'react-dom/client'
import formatMessage from 'format-message'
import { DrawerPortal } from './components/DrawerPortal'
import { convertStringBool } from '@Utils'

import { IconBar } from './components/IconBar'
import { initNarration } from './utils/narrateElementLoader'
import { ensureAIToolsDefined } from './utils/aiToolsLoader'
import { getAIToolsGates } from './utils/aiToolsGating'
import { attachAIToolsPreloadRetry } from './utils/aiToolsPreloadRetry'
import {
  getAIToolStartupTracker,
  initializeReaderAIBootstrap,
} from './utils/aiToolsStartup'

formatMessage.setup({
  // don't console.warn or throw an error when a translation is missing
  missingTranslation: 'ignore',
})

const registerCloseChatDrawerEvent = courseNum => {
  window.addEventListener('close-chat-drawer', () => {
    if (!document.body.classList.contains('has-chat-drawer')) return

    document.body.classList.remove('has-chat-drawer')

    const readerLayout = document.getElementById('reader_layout')

    if (readerLayout) {
      readerLayout.style.marginRight = ''
    }

    const arrow = document.querySelector('.navigation_arrow_right')
    if (arrow) arrow.style.right = ''

    // persist closed state
    try {
      localStorage.setItem(`chatDisplayWin-${courseNum}`, 'false')
    } catch (e) {
      console.error('Non-critical error:', e)
    }
  })
}

const openChatDrawerForCourse = courseNum => {
  if (!document.body.classList.contains('has-chat-drawer')) {
    document.body.classList.add('has-chat-drawer')
  }

  try {
    localStorage.setItem(`chatDisplayWin-${courseNum}`, 'true')
    localStorage.setItem(`chatActiveDrawer-${courseNum}`, 'assistant')
  } catch (e) {
    // ignore
  }

  const readerLayout = document.getElementById('reader_layout')
  const iconbar = document.getElementById('reader_iconbar')
  const drawer = document.getElementById('chatbot_drawer')
  if (readerLayout && iconbar && drawer) {
    const iconbarWidth = iconbar.offsetWidth
    const drawerWidth = drawer.offsetWidth
    const arrow = document.querySelector('.navigation_arrow_right')
    readerLayout.style.marginRight = `${drawerWidth + iconbarWidth}px`
    if (arrow) arrow.style.right = `${drawerWidth + iconbarWidth + 30}px`
  }

  const evt = new CustomEvent('drawer-state-change', {
    detail: { activeDrawer: 'assistant' },
  })
  document.dispatchEvent(evt)
}

const registerAichatEventOpeners = courseNum => {
  const handler = event => {
    if (!event || !event.detail) return
    if (event.detail.coursenum !== courseNum) return
    openChatDrawerForCourse(courseNum)
  }

  window.addEventListener('aichat-messages-updated', handler)
  window.addEventListener('aichat-question-trigger', handler)
  window.addEventListener('aichat-input-prefill', handler)
}

const parseRecentPageIds = () => {
  const drawer = document.getElementById('chatbot_drawer')
  const raw = drawer?.dataset?.recentPageIds
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(Boolean) : []
  } catch (error) {
    console.error('Failed to parse recent page ids for Quick Review:', error)
    return []
  }
}

const parseUpcomingAssessments = () => {
  const drawer = document.getElementById('chatbot_drawer')
  const raw = drawer?.dataset?.upcomingAssessments
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(Boolean) : []
  } catch (error) {
    console.error('Failed to parse upcoming assessments for Quick Review:', error)
    return []
  }
}

const parseAIFeatures = () => {
  const drawer = document.getElementById('chatbot_drawer')
  const raw = drawer?.dataset?.aiFeatures
  if (!raw) {
    return {}
  }

  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    console.error('Failed to parse AI feature bootstrap data:', error)
    return {}
  }
}

const getVoshiAppsVersion = () => {
  const drawer = document.getElementById('chatbot_drawer')
  return drawer?.dataset?.voshiAppsVersion || ''
}

const preloadAITools = async ({
  coursenum,
  pageid,
  userid,
  recentPageIds,
  upcomingAssessments,
  aiFeatures,
}) => {
  const tracker = getAIToolStartupTracker()
  const { preloadBundle, prefetchQuickReview } = getAIToolsGates({
    aiFeatures,
    userid,
  })

  if (!preloadBundle) {
    return
  }

  try {
    const isDefined = await ensureAIToolsDefined()
    if (!isDefined) {
      return
    }

    if (!prefetchQuickReview) {
      return
    }

    const prefetch = window.__myeducatorAIToolsApi?.prefetchQuickReviewSessionOptions
    if (typeof prefetch !== 'function') {
      return
    }

    prefetch({
      coursenum,
      pageid,
      userid,
      recentPageIds,
      upcomingAssessments,
    }).catch(error => {
      console.warn('Quick Review prefetch failed:', error)
    })
  } catch (error) {
    tracker.markOnce('aitools-preload-failed', { message: error?.message || String(error) })
  }
}

const initNavArrowResponsive = (isInitial = false, thresholdPx = 767) => {
  const readerContent = document.getElementById('reader_content')
  const nav = readerContent ? readerContent.querySelector('nav') : null
  if (!readerContent || !nav || typeof ResizeObserver === 'undefined') return

  nav.classList.add('navigation-arrows')

  const update = () => {
    if (readerContent.clientWidth < thresholdPx) {
      if (readerContent.clientWidth < 100) {
        nav.style.left = '50%'
        nav.style.transform = 'translateX(-50%)'
      } else {
        const rect = readerContent.getBoundingClientRect()
        if (rect.width > 0 && rect.left >= 0) {
          const readerCenterX = rect.left + rect.width / 2

          if (readerCenterX > 0 && readerCenterX <= window.innerWidth) {
            nav.style.left = `${readerCenterX}px`
            nav.style.transform = 'translateX(-50%)'
          } else {
            nav.style.left = '50%'
            nav.style.transform = 'translateX(-50%)'
          }
        } else {
          nav.style.left = '50%'
          nav.style.transform = 'translateX(-50%)'
        }
      }
      nav.classList.add('float-arrows')
    } else {
      nav.classList.remove('float-arrows')
      nav.style.left = ''
      nav.style.transform = ''
    }
  }

  const stableUpdate = () => {
    // If initial load, update synchronously without animation frames
    if (isInitial) {
      update()
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          update()
        })
      })
    }
  }

  const ro = new ResizeObserver(update)
  ro.observe(readerContent)

  if (document.readyState === 'complete') {
    stableUpdate()
    setTimeout(stableUpdate, 100)
  } else {
    window.addEventListener('load', () => {
      stableUpdate()
      setTimeout(stableUpdate, 100)
      setTimeout(stableUpdate, 300)
    })

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(stableUpdate, 50)
      })
    } else {
      setTimeout(stableUpdate, 50)
    }
  }

  // Remove loading class after initial positioning is complete
  if (isInitial) {
    // Use a longer timeout to ensure all elements are fully rendered and positioned
    setTimeout(() => {
      // Final check to ensure drawer is properly positioned if open
      try {
        const iconbarEl = document.getElementById('reader_iconbar')
        const coursenum =
          iconbarEl && iconbarEl.dataset && iconbarEl.dataset.coursenum
        if (coursenum) {
          const stored = localStorage.getItem('chatDisplayWin-' + coursenum)
          if (stored === 'true') {
            const readerLayout = document.getElementById('reader_layout')
            const drawer = document.getElementById('chatbot_drawer')

            if (readerLayout && iconbarEl && drawer) {
              const iconbarWidth = iconbarEl.offsetWidth
              const drawerWidth = drawer.offsetWidth
              const arrow = document.querySelector('.navigation_arrow_right')

              // Final position adjustment before enabling transitions
              readerLayout.style.marginRight = drawerWidth + iconbarWidth + 'px'
              if (arrow)
                arrow.style.right = drawerWidth + iconbarWidth + 30 + 'px'
            }
          }
        }
      } catch (e) {
        console.error('Non-critical error during final positioning:', e)
      }

      // Now it's safe to remove the loading class and enable transitions
      document.body.classList.remove('loading')
    }, 100) // Longer timeout to ensure everything is fully rendered
  }
}

const initChatbotDrawerResizer = courseNum => {
  // Move these declarations to the very top
  const drawer = document.getElementById('chatbot_drawer')
  if (!drawer) return

  const iconbar = document.getElementById('reader_iconbar')
  const readerLayout = document.getElementById('reader_layout')

  const MIN_WIDTH = 300
  const MOBILE_BREAKPOINT = 767
  let MAX_WIDTH = Math.floor(window.innerWidth * 0.6)

  // Declare these at function scope so they're accessible everywhere
  let isHovered = false
  let tooltipTimer = null
  let isResizing = false
  let bar = null
  let tooltip = null
  let showBar = () => {}
  let hideBar = () => {}
  let mouseDownPosition = null // Track mouse position for click detection

  if (!handle) {
    handle = document.createElement('div')
    handle.className = 'resize-handle resize-handle-right'
    handle.setAttribute('role', 'separator')
    handle.setAttribute('aria-orientation', 'vertical')
    handle.setAttribute(
      'aria-label',
      'Drag to resize chat panel, or click to close'
    )
    handle.setAttribute('tabindex', '0')

    const hitArea = document.createElement('div')
    hitArea.style.cssText = `
      position: absolute;
      width: 10px;
      top: 0;
      right: -8px;
      bottom: 0;
      cursor: col-resize;
    `

    bar = document.createElement('div')
    bar.className = 'resize-bar'
    bar.style.cssText = `
      position: relative;
      width: 2px;
      height: 100%;
      background-color: #004876;
      opacity: 0;
      transition: opacity 0.2s ease;
    `

    tooltip = document.createElement('div')
    tooltip.className = 'resize-tooltip'
    tooltip.style.cssText = `
      position: fixed;
      background-color: #1e293b;
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 400;
      pointer-events: none;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      z-index: 10000000;
      display: none;
      transform: translateY(-50%);
    `

    const line1 = document.createElement('span')
    line1.textContent = 'Drag to resize'
    line1.style.cssText = 'display: block; white-space: nowrap;'

    const line2 = document.createElement('span')
    line2.textContent = 'Click to close'
    line2.style.cssText = 'display: block; white-space: nowrap;'

    tooltip.appendChild(line1)
    tooltip.appendChild(line2)

    handle.appendChild(hitArea)
    handle.appendChild(bar)
    drawer.appendChild(handle)
    document.body.appendChild(tooltip)

    // Define show/hide functions with access to bar + hover state
    showBar = () => {
      if (bar) bar.style.opacity = '1'
    }

    hideBar = () => {
      // Only hide if not resizing AND not hovering
      if (!isResizing && !isHovered && bar) {
        bar.style.opacity = '0'
      }
    }

    const showTooltip = clientY => {
      // Don't show tooltip if we're currently resizing
      if (isResizing || !tooltip) return

      tooltip.style.display = 'flex'
      tooltip.style.flexDirection = 'column'
      tooltip.style.gap = '2px'
      tooltip.style.top = `${clientY}px`
      const drawerWidth = drawer.offsetWidth
      const handlePos = window.innerWidth - drawerWidth
      tooltip.style.left = `${handlePos - 150}px`
    }

    const hideTooltip = () => {
      if (!tooltip) return
      tooltip.style.display = 'none'
      if (tooltipTimer) {
        clearTimeout(tooltipTimer)
        tooltipTimer = null
      }
    }

    // Hover handlers
    handle.addEventListener('mouseenter', e => {
      isHovered = true
      showBar()

      // Don't start tooltip timer if we're resizing
      if (!isResizing) {
        tooltipTimer = setTimeout(() => {
          if (isHovered && !isResizing) showTooltip(e.clientY)
        }, 500)
      }
    })

    handle.addEventListener('mousemove', e => {
      if (
        isHovered &&
        tooltip &&
        tooltip.style.display === 'block' &&
        !isResizing
      ) {
        tooltip.style.top = `${e.clientY}px`
      }
    })

    handle.addEventListener('mouseleave', () => {
      isHovered = false
      hideBar()
      hideTooltip()
    })

    // Click handler for closing drawer
    handle.addEventListener('click', e => {
      // Check if this was a click (not a drag)
      if (mouseDownPosition) {
        const deltaX = Math.abs(e.clientX - mouseDownPosition.x)
        const deltaY = Math.abs(e.clientY - mouseDownPosition.y)

        // If mouse moved less than 5px in either direction, treat it as a click
        if (deltaX < 5 && deltaY < 5) {
          window.dispatchEvent(
            new CustomEvent('close-chat-drawer', {
              detail: { fromCoordinator: false },
            })
          )
        }
      }

      mouseDownPosition = null
    })

    // Keyboard handler for accessibility
    handle.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        window.dispatchEvent(
          new CustomEvent('close-chat-drawer', {
            detail: { fromCoordinator: false },
          })
        )
      }
    })
  }

  const updateHandlePosition = () => {
    const drawerWidth = drawer.offsetWidth
    handle.style.right = `${drawerWidth - 3}px`
    handle.style.left = 'auto'
  }

  const updateLayoutOffsets = () => {
    const iconbarWidth = iconbar ? iconbar.offsetWidth : 0
    const drawerWidth = drawer.offsetWidth
    const total = drawerWidth + iconbarWidth

    if (document.body.classList.contains('has-chat-drawer')) {
      if (readerLayout) readerLayout.style.marginRight = `${total}px`
      const arrow = document.querySelector('.navigation_arrow_right')
      if (arrow) arrow.style.right = `${total + 30}px`
    }
  }

  const updateRightArrow = offset => {
    const arrow = document.querySelector('.navigation_arrow_right')
    if (arrow) {
      arrow.style.right = `${offset}px`
    }
  }

  const getConstrainedWidth = width => {
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      return window.innerWidth
    }
    return Math.max(MIN_WIDTH, Math.min(width, MAX_WIDTH))
  }

  const persistWidth = w => {
    try {
      localStorage.setItem(`chatDrawerWidth-${courseNum}`, String(w))
    } catch (err) {
      console.log('error', err)
    }
  }

  const applyWidth = w => {
    const constrainedWidth = getConstrainedWidth(w)
    const iconbarWidth = iconbar ? iconbar.offsetWidth : 0

    drawer.style.width = `${constrainedWidth}px`

    if (document.body.classList.contains('has-chat-drawer')) {
      if (readerLayout)
        readerLayout.style.marginRight = `${constrainedWidth + iconbarWidth}px`
      updateRightArrow(constrainedWidth + iconbarWidth + 20)
    } else {
      if (readerLayout) readerLayout.style.marginRight = ''
    }

    if (constrainedWidth !== w) {
      persistWidth(constrainedWidth)
    }

    updateHandlePosition()
  }

  const handleWindowResize = () => {
    MAX_WIDTH = Math.floor(window.innerWidth * 0.6)
    const currentWidth = drawer.offsetWidth
    applyWidth(currentWidth)
  }

  // Load saved width
  try {
    const saved = localStorage.getItem(`chatDrawerWidth-${courseNum}`)
    if (saved) {
      const parsed = parseInt(saved, 10)
      if (!Number.isNaN(parsed)) applyWidth(parsed)
    }
  } catch (err) {
    console.log(err)
  }

  // Create or find the resize handle
  let handle = drawer.querySelector('.resize-handle')

  const startResize = evt => {
    evt.preventDefault()

    // Store mouse down position for click detection
    const isTouch = evt.type === 'touchstart'
    const clientX = isTouch ? evt.touches[0].clientX : evt.clientX
    const clientY = isTouch ? evt.touches[0].clientY : evt.clientY
    mouseDownPosition = { x: clientX, y: clientY }

    const startX = isTouch ? evt.touches[0].clientX : evt.pageX
    const startWidth = drawer.offsetWidth

    isResizing = true
    document.body.classList.add('resizing-right')
    showBar()

    // Hide tooltip when resize starts
    if (tooltip) {
      tooltip.style.display = 'none'
      if (tooltipTimer) {
        clearTimeout(tooltipTimer)
        tooltipTimer = null
      }
    }

    // Disable transition during resize
    if (bar) {
      bar.style.transition = 'none'
    }

    const onMove = moveEvt => {
      const moveX = isTouch ? moveEvt.touches[0].clientX : moveEvt.pageX
      const delta = moveX - startX
      const newWidth = startWidth - delta
      applyWidth(newWidth)
    }

    const onEnd = () => {
      document.body.removeEventListener(
        isTouch ? 'touchmove' : 'mousemove',
        onMove
      )
      document.body.removeEventListener(isTouch ? 'touchend' : 'mouseup', onEnd)

      // Re-enable transition immediately so the fade out is smooth
      if (bar) {
        bar.style.transition = 'opacity 0.2s ease'
      }

      // Clear resizing flag immediately
      isResizing = false

      setTimeout(() => {
        document.body.classList.remove('resizing-right')
      }, 100)

      persistWidth(drawer.offsetWidth)

      // Hide bar after resize ends (if not hovering) - now with transition
      hideBar()
    }

    document.body.addEventListener(isTouch ? 'touchmove' : 'mousemove', onMove)
    document.body.addEventListener(isTouch ? 'touchend' : 'mouseup', onEnd)
  }

  handle.addEventListener('mousedown', startResize)
  handle.addEventListener('touchstart', startResize)

  window.addEventListener('resize', handleWindowResize)

  updateHandlePosition()
  updateLayoutOffsets()

  const arrowObserver = new MutationObserver(() => {
    const arrow = document.querySelector('.navigation_arrow_right')
    if (arrow) {
      updateLayoutOffsets()
      arrowObserver.disconnect()
    }
  })
  arrowObserver.observe(document.body, { childList: true, subtree: true })
}

const applyRightInitialLayout = coursenum => {
  // Add loading class to disable all transitions
  document.body.classList.add('loading')

  try {
    const iconbar = document.getElementById('reader_iconbar')
    const stored = localStorage.getItem('chatDisplayWin-' + coursenum)
    const activeDrawer = localStorage.getItem('chatActiveDrawer-' + coursenum)

    // Check if assistant is enabled - safely handle undefined
    const assistantEnabledAttr = iconbar && iconbar.dataset.assistantEnabled
    const assistantEnabled = assistantEnabledAttr === 'true'

    // If drawer was open to assistant but assistant is now disabled, close it
    if (stored === 'true' && activeDrawer === 'assistant') {
      // Only proceed if we can definitively determine assistant is disabled
      // If undefined, we'll let IconBar handle it when it mounts
      if (assistantEnabledAttr !== undefined && !assistantEnabled) {
        localStorage.setItem('chatDisplayWin-' + coursenum, 'false')
        localStorage.removeItem('chatActiveDrawer-' + coursenum)
        document.body.classList.remove('has-chat-drawer')

        const readerLayout = document.getElementById('reader_layout')
        const arrow = document.querySelector('.navigation_arrow_right')
        if (readerLayout) {
          readerLayout.style.marginRight = ''
        }
        if (arrow) {
          arrow.style.right = ''
        }
        return
      }
    }

    if (stored === 'false') {
      document.body.classList.remove('has-chat-drawer')

      const readerLayout = document.getElementById('reader_layout')
      const arrow = document.querySelector('.navigation_arrow_right')
      if (readerLayout) {
        readerLayout.style.marginRight = ''
      }
      if (arrow) {
        arrow.style.right = ''
      }
      return
    }

    if (stored === 'true') {
      // Apply drawer open state immediately
      document.body.classList.add('has-chat-drawer')

      const readerLayout = document.getElementById('reader_layout')
      const drawer = document.getElementById('chatbot_drawer')

      if (readerLayout && iconbar && drawer) {
        const iconbarWidth = iconbar.offsetWidth
        const drawerWidth = drawer.offsetWidth
        const arrow = document.querySelector('.navigation_arrow_right')

        readerLayout.style.marginRight = drawerWidth + iconbarWidth + 'px'
        if (arrow) arrow.style.right = drawerWidth + iconbarWidth + 30 + 'px'
      }
    }
  } catch (e) {
    console.error('Non-critical error during initial layout:', e)
  }
}

export let startReader = (
  coursenum,
  _,
  notebook,
  authorTools,
  userRole,
  chapterID,
  pageID,
  showSectionNumbers,
  userId,
  userEmail,
  demoAccess
) => {
  // Apply initial layout before any rendering
  applyRightInitialLayout(coursenum)

  let allowNotebook = convertStringBool(notebook)
  let allowAuthorTools = convertStringBool(authorTools)

  initChatbotDrawerResizer(coursenum)
  registerCloseChatDrawerEvent(coursenum)
  initNavArrowResponsive(true) // Pass isInitial=true
  registerAichatEventOpeners(coursenum)

  initNarration()

  const aiFeatures = parseAIFeatures()
  initializeReaderAIBootstrap({
    coursenum,
    aiFeatures,
    voshiAppsVersion: getVoshiAppsVersion(),
  })

  try {
    const stored = localStorage.getItem(`chatDisplayWin-${coursenum}`)

    if (stored === 'false') {
      document.body.classList.remove('has-chat-drawer')

      const readerLayout = document.getElementById('reader_layout')
      const arrow = document.querySelector('.navigation_arrow_right')
      if (readerLayout) {
        readerLayout.style.marginRight = ''
      }
      if (arrow) {
        arrow.style.right = ''
      }
    } else if (stored === 'true') {
      document.body.classList.add('has-chat-drawer')
      const readerLayout = document.getElementById('reader_layout')
      const iconbar = document.getElementById('reader_iconbar')
      const drawer = document.getElementById('chatbot_drawer')

      if (readerLayout && iconbar && drawer) {
        const iconbarWidth = iconbar.offsetWidth
        const drawerWidth = drawer.offsetWidth

        const arrow = document.querySelector('.navigation_arrow_right')

        // Apply layout immediately without animation (loading class is already applied)
        readerLayout.style.marginRight = `${drawerWidth + iconbarWidth}px`
        if (arrow) arrow.style.right = `${drawerWidth + iconbarWidth + 30}px`
      }
    }
  } catch (e) {
    console.error('Non-critical error:', e)
  }

  const iconBarEl = document.getElementById('reader_iconbar')
  function IconBarPortal(props) {
    return iconBarEl && createPortal(<IconBar {...props} />, iconBarEl)
  }

  const effectivePageID = pageID || chapterID
  const assistantEnabled = iconBarEl.dataset.assistantEnabled === 'true'
  const recentPageIds = parseRecentPageIds()
  const upcomingAssessments = parseUpcomingAssessments()

  void preloadAITools({
    coursenum,
    pageid: effectivePageID,
    userid: userId,
    recentPageIds,
    upcomingAssessments,
    aiFeatures,
  })
  attachAIToolsPreloadRetry(
    {
      coursenum,
      pageid: effectivePageID,
      userid: userId,
      recentPageIds,
      upcomingAssessments,
      aiFeatures,
    },
    preloadAITools
  )

  const App = () => (
    <React.Fragment>
      <IconBarPortal
        coursenum={coursenum}
        pageID={pageID}
        userId={userId}
        userEmail={userEmail}
        allowNotebook={allowNotebook}
        allowAuthorTools={allowAuthorTools}
        userRole={userRole}
        assistantEnabled={assistantEnabled}
        demoAccess={demoAccess}
      />
      <DrawerPortal
        userId={userId}
        coursenum={coursenum}
        pageId={effectivePageID}
        recentPageIds={recentPageIds}
        upcomingAssessments={upcomingAssessments}
        allowAuthorTools={allowAuthorTools}
        userRole={userRole}
        userEmail={userEmail}
        assistantEnabled={assistantEnabled}
      />
    </React.Fragment>
  )

  const root = createRoot(document.getElementById('reader_app_root'))
  root.render(<App />)
}

// The right toolbar on the book's title page (web.coverpage.html). Only the toolbar and the
// book-wide drawers (search, glossary, settings, contact) are booted: there is no book content,
// so no narration, nav arrows, AI bootstrap or preload. Page-bound tools (highlighting, notebook,
// quick review, assistant) render greyed out via IconBar's context="coverpage"; the AI drawers
// are unusable (assistantEnabled=false) because the cover carries its own inline AI chat.
//
// Deliberately NOT called here: applyRightInitialLayout adds body.loading, which only
// initNavArrowResponsive removes (and that returns early without #reader_content);
// registerAichatEventOpeners would slide open an empty drawer when the inline chat fires.
export let startCoverpageReader = (coursenum, opts = {}) => {
  const iconBarEl = document.getElementById('reader_iconbar')
  const rootEl = document.getElementById('coverpage-reader-root')
  if (!iconBarEl || !rootEl) return

  registerCloseChatDrawerEvent(coursenum)
  initChatbotDrawerResizer(coursenum)

  function IconBarPortal(props) {
    return createPortal(<IconBar {...props} />, iconBarEl)
  }

  const allowNotebook = !!opts.allowNotebook
  const allowAuthorTools = !!opts.allowAuthorTools

  const App = () => (
    <React.Fragment>
      <IconBarPortal
        context="coverpage"
        coursenum={coursenum}
        pageID=""
        userId={opts.userId}
        userEmail={opts.userEmail}
        allowNotebook={allowNotebook}
        allowAuthorTools={allowAuthorTools}
        userRole={opts.userRole}
        assistantEnabled={false}
        courseAssistantEnabled={!!opts.courseAssistantEnabled}
        demoAccess={!!opts.demoAccess}
      />
      <DrawerPortal
        userId={opts.userId}
        coursenum={coursenum}
        pageId=""
        recentPageIds={[]}
        upcomingAssessments={[]}
        allowAuthorTools={allowAuthorTools}
        userRole={opts.userRole}
        userEmail={opts.userEmail}
        assistantEnabled={false}
      />
    </React.Fragment>
  )

  const root = createRoot(rootEl)
  root.render(<App />)
}
